import React, {useEffect, useRef, useState} from "react";
import Styles from "./CardList.module.scss";
import { CardItem } from "../../components";
import { useAppDispatch } from "../../redux/store";
import { useSelector } from "react-redux";
import { getCardSelector } from "../../redux/profile/cards/selectors";
import { getProfileInfo } from "../../redux/profile/settings/getProfileInfo/selectors";
import { fetchGetCards } from "../../redux/profile/cards/asyncActions";
import { fetchCreateCard } from "../../redux/profile/cards/asyncActions";
import { fetchProfileInfo } from "../../redux/profile/settings/getProfileInfo/asyncActions";
import { axiosInstance } from "../../resources/requestInfo";
import cardIcon from "../../assets/icons/card.svg";

import { CardListPropsType } from "./types";
import { resetCardsState, setCardid, addUserCardRedux } from "../../redux/profile/cards/slice";
import { CardListSkeleton } from "./CardListSkeleton";
import { CommonErrorModal } from "../CommonErrorModal";
import { isTinkoffProvider } from "@/utils/payment/paymentProvider";
import { cardsRoutes } from "@redux/profile/cards/routes";

// Глобальный тип для виджета YooKassa
declare global {
  interface Window {
    YooMoneyCheckoutWidget?: any;
  }
}

function loadYooKassaWidget(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.YooMoneyCheckoutWidget) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://yookassa.ru/checkout-widget/v1/checkout-widget.js';
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load YooKassa widget script'));
    document.head.appendChild(script);
  });
}

export const CardList: React.FC<CardListPropsType> = (props) => {
  const { onPage = "profile", onClickFunc } = props;

  const { cards, isLoading } = useSelector(getCardSelector);
  const { profileInfo } = useSelector(getProfileInfo);
  const dispatch = useAppDispatch();

  let selectedItem: any = null;

  // Card binding state
  const [bindingState, setBindingState] = useState<'idle' | 'loading' | 'widget' | 'processing'>('idle');
  const [bindingPaymentId, setBindingPaymentId] = useState<string | null>(null);
  // Т-Касса: привязка идёт своей формой банка (AddCard), а не виджетом ЮKassa.
  const [tinkoffFormUrl, setTinkoffFormUrl] = useState<string | null>(null);
  const isTinkoff = isTinkoffProvider();
  const [showErrorModal, setShowErrorModal] = useState(false);
  const widgetContainerRef = useRef<HTMLDivElement>(null);
  const widgetInstanceRef = useRef<any>(null);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    dispatch(fetchGetCards());
    return () => {
      dispatch(resetCardsState());
    };
  }, [dispatch]);

  // Cleanup widget and polling on unmount
  useEffect(() => {
    return () => {
      if (widgetInstanceRef.current) {
        try {
          widgetInstanceRef.current.destroy();
        } catch (e) {
          // ignore
        }
        widgetInstanceRef.current = null;
      }
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, []);

  const stopPolling = () => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  };

  const destroyWidget = () => {
    if (widgetInstanceRef.current) {
      try {
        widgetInstanceRef.current.destroy();
      } catch (e) {
        // ignore
      }
      widgetInstanceRef.current = null;
    }
  };

  const resetBindingState = () => {
    stopPolling();
    destroyWidget();
    setBindingState('idle');
    setBindingPaymentId(null);
    setTinkoffFormUrl(null);
  };

  const showError = () => {
    resetBindingState();
    setShowErrorModal(true);
  };

  const handleBindingSuccess = async (paymentId: string) => {
    stopPolling();
    destroyWidget();
    setBindingState('processing');

    try {
      // Get payment info to extract card data
      const statusRes = await axiosInstance.get(`/payments/${paymentId}/status`);
      const paymentData = statusRes.data;

      const token = paymentData.payment_method?.id;
      const last4 = paymentData.payment_method?.card?.last4;

      if (!token || !last4) {
        throw new Error('Не удалось извлечь данные карты из платежа');
      }

      // Ensure we have profile info
      let currentProfileInfo = profileInfo;
      if (!currentProfileInfo?.id) {
        await dispatch(fetchProfileInfo());
      }

      const userId = currentProfileInfo?.id;
      if (!userId) {
        throw new Error('Не удалось определить ID пользователя');
      }

      // Save card to backend
      const cardTitle = `**** **** **** ${last4}`;
      const newCard = await dispatch(
        fetchCreateCard({
          user_id: userId,
          token: token,
          title: cardTitle,
        })
      ).unwrap();

      dispatch(addUserCardRedux(newCard));

      // Refund the 11 RUB test payment
      try {
        await axiosInstance.post(`/payments/${paymentId}/refund`);
      } catch (refundError) {
        console.warn('Не удалось вернуть тестовый платеж:', refundError);
      }

      // Refresh card list
      await dispatch(fetchGetCards());

      setBindingState('idle');
      setBindingPaymentId(null);
    } catch (err: any) {
      console.error('Ошибка при сохранении карты:', err);
      showError();
    }
  };

  const startPolling = (paymentId: string) => {
    stopPolling();

    let attempts = 0;
    const maxAttempts = 200; // ~10 minutes at 3s intervals

    pollingRef.current = setInterval(async () => {
      attempts++;
      if (attempts > maxAttempts) {
        showError();
        return;
      }

      try {
        const res = await axiosInstance.get(`/payments/${paymentId}/status`);
        const status = res.data?.status;

        if (status === 'succeeded') {
          await handleBindingSuccess(paymentId);
        } else if (status === 'canceled') {
          showError();
        }
      } catch (err) {
        // Ignore polling errors
      }
    }, 3000);
  };

  /**
   * Т-Касса: результат привязки приходит не платежом, а состоянием заявки
   * (GetAddCardState по RequestKey). Успех — карта уже лежит в «кошельке»
   * покупателя на стороне банка, своей записи в cards заводить не нужно.
   */
  const startTinkoffBindingPolling = (requestKey: string) => {
    stopPolling();

    let attempts = 0;
    const maxAttempts = 200; // ~10 минут при интервале 3 с

    pollingRef.current = setInterval(async () => {
      attempts++;
      if (attempts > maxAttempts) {
        showError();
        return;
      }

      try {
        const res = await axiosInstance.get(cardsRoutes.TINKOFF_CARD_STATE(requestKey));
        const status = res.data?.status;

        if (status === 'succeeded') {
          stopPolling();
          setBindingState('processing');
          await dispatch(fetchGetCards());
          resetBindingState();
        } else if (status === 'canceled') {
          showError();
        }
      } catch (err) {
        // Ошибки опроса игнорируем — ждём следующую попытку
      }
    }, 3000);
  };

  const initBindingWidget = async (confirmationToken: string) => {
    try {
      await loadYooKassaWidget();

      destroyWidget();

      const checkout = new window.YooMoneyCheckoutWidget({
        confirmation_token: confirmationToken,
        error_callback: (error: any) => {
          console.error('YooKassa widget error:', error);
          showError();
        },
      });

      widgetInstanceRef.current = checkout;

      // Small delay to ensure container is rendered
      setTimeout(async () => {
        try {
          await checkout.render('card-binding-widget-container');
          setBindingState('widget');
        } catch (renderError) {
          console.error('Widget render error:', renderError);
          showError();
        }
      }, 100);
    } catch (error) {
      console.error('Ошибка инициализации виджета:', error);
      showError();
    }
  };

  const handlePaymentClick = async () => {
    if (bindingState !== 'idle') return;

    try {
      setBindingState('loading');

      // Ensure profile info is loaded
      let currentProfileInfo = profileInfo;
      if (!currentProfileInfo?.phone) {
        await dispatch(fetchProfileInfo());
      }

      if (isTinkoff) {
        const res = await axiosInstance.post(cardsRoutes.TINKOFF_CARDS);
        const { request_key, payment_url } = res.data || {};

        if (!request_key || !payment_url) {
          throw new Error('Не удалось получить форму привязки карты');
        }

        setTinkoffFormUrl(payment_url);
        setBindingState('widget');
        startTinkoffBindingPolling(request_key);
        return;
      }

      // Call backend to create card binding payment
      const res = await axiosInstance.post('/payments/bind-card', {
        phone: currentProfileInfo?.phone || '',
      });

      const { id, confirmation_token } = res.data;

      if (!confirmation_token) {
        throw new Error('Не удалось получить токен для привязки карты');
      }

      setBindingPaymentId(id);

      // Start polling for payment status
      startPolling(id);

      // Initialize widget
      await initBindingWidget(confirmation_token);
    } catch (error: any) {
      console.error('Ошибка при привязке карты:', error);
      showError();
    }
  };

  const handleCancelBinding = () => {
    resetBindingState();
  };

  const addSelectStyles = React.useCallback((e: any) => {
    if (onPage === "payment") {
      const target = e.target.closest("[data-type]");

      if (target) {
        if (selectedItem) {
          selectedItem.style.boxShadow = "";
        }

        selectedItem = target;
        selectedItem.style.boxShadow = "2px 2px 15px rgba(140, 179, 70, 1)";
      }
    }
  }, []);

  const handleClickOtherMethods = (e: any) => {
    addSelectStyles(e);
    dispatch(setCardid(0));
    if (onClickFunc) onClickFunc();
  };

  const handleSelectItem = (id: number) => {
    dispatch(setCardid(id));
    if (onClickFunc) onClickFunc();
  };

  // Widget is shown - render only the widget
  if (bindingState === 'widget' || bindingState === 'loading') {
    return (
      <>
        <p
          className={Styles.subTitile}
          style={onPage === "payment" ? { display: "block" } : {}}
        >
          Привязка карты
        </p>

        {bindingState === 'loading' && (
          <div className={Styles.item_plusItem} style={{ justifyContent: 'center', cursor: 'default' }}>
            <span>Загрузка формы оплаты...</span>
          </div>
        )}

        {tinkoffFormUrl ? (
          <div className={Styles.bindingFrameWrap}>
            <iframe
              title="Привязка карты Т-Касса"
              src={tinkoffFormUrl}
              className={Styles.bindingFrame}
              allow="payment *"
            />
            <p className={Styles.bindingFallback}>
              Форма не открылась?{' '}
              <a href={tinkoffFormUrl} target="_blank" rel="noopener noreferrer">
                Открыть в новом окне
              </a>
            </p>
          </div>
        ) : (
          <div
            id="card-binding-widget-container"
            ref={widgetContainerRef}
            style={{ display: bindingState === 'widget' ? 'block' : 'none', marginBottom: '16px' }}
          />
        )}

        <div
          className={Styles.item_plusItem}
          onClick={handleCancelBinding}
          style={{ justifyContent: 'center', marginTop: '8px' }}
        >
          <span>Отмена</span>
        </div>

        <CommonErrorModal
          isOpen={showErrorModal}
          setIsOpen={setShowErrorModal}
          closeFunc={() => setShowErrorModal(false)}
        />
      </>
    );
  }

  // Processing state
  if (bindingState === 'processing') {
    return (
      <>
        <p
          className={Styles.subTitile}
          style={onPage === "payment" ? { display: "block" } : {}}
        >
          Привязка карты
        </p>
        <div className={Styles.item_plusItem} style={{ justifyContent: 'center', cursor: 'default' }}>
          <span>Сохранение карты...</span>
        </div>

        <CommonErrorModal
          isOpen={showErrorModal}
          setIsOpen={setShowErrorModal}
          closeFunc={() => setShowErrorModal(false)}
        />
      </>
    );
  }

  return (
    <>
      {isLoading ? (
        <CardListSkeleton />
      ) : (
        <>
          <p
            className={Styles.subTitile}
            style={onPage === "payment" ? { display: "block" } : {}}
          >
            Мои карты
          </p>
          <div className={Styles.item} onClick={addSelectStyles}>
            {!!cards &&
              (cards.length > 0 &&
                cards.map((item) =>
                  <CardItem
                      data={item}
                      key={item.id}
                      dataType="cardItem"
                      onPage={onPage}
                      selectFunc={handleSelectItem}
                  />
                )
              )
            }

            <div onClick={handlePaymentClick} data-type="cardItem" className={Styles.item_plusItem}>
              <span>Привязать банковскую карту</span>
            </div>
          </div>
        </>
      )}
      {onPage === "payment" ? (
        <>
          <p className={Styles.subTitile__otherMethods}>Другие способы</p>
          <div
            className={Styles.otherMethodsItem}
            data-type="cardItem"
            onClick={handleClickOtherMethods}
            onLoad={handleClickOtherMethods}
          >
            <img src={cardIcon} />
            <div>Оплатить другим способом</div>
          </div>
        </>
      ) : null}

      <CommonErrorModal
        isOpen={showErrorModal}
        setIsOpen={setShowErrorModal}
        closeFunc={() => setShowErrorModal(false)}
      />
    </>
  );
};
